import {
  Box,
  Divider,
  Icon,
  Input,
  Link,
  Select as ChakraSelect,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { Download } from "lucide-react";
import type { FileItem, FormField, Submission } from "../../../../types/onboarding";

// ─── Shared utils ─────────────────────────────────────────────────────────────

// Parseia o value de um campo file_list (array JSON de URLs). Legado (path
// único não-JSON) vira lista de 1 item. Usado para renderizar múltiplos links.
const parseFileListValue = (v: string | null | undefined): string[] => {
  if (!v) return [];
  try {
    const parsed = JSON.parse(v);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    if (typeof parsed === "string") return [parsed];
    return [];
  } catch {
    return [v];
  }
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.Z+-]*)?$/;

export const formatValue = (v: string | null | undefined): string => {
  if (v === null || v === undefined || v.trim() === "" || v.toLowerCase() === "null") return "NÃO INFORMADO";
  if (v === "true")  return "Sim";
  if (v === "false") return "Não";
  if (ISO_DATE_RE.test(v.trim())) {
    const [year, month, day] = v.trim().split("T")[0].split("-");
    return `${day}/${month}/${year}`;
  }
  return v;
};

export const isFileLink = (v: string | null | undefined): boolean => {
  if (!v) return false;
  const s = v.trim();
  return (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("gs://") ||
    s.includes("X-Goog-") ||
    s.includes("X-Amz-") ||
    s.includes("storage.googleapis.com") ||
    /\.(pdf|jpg|jpeg|png|doc|docx|xls|xlsx|csv|zip)\?/i.test(s)
  );
};

// ─── FileLink ─────────────────────────────────────────────────────────────────

const FileLink = ({ href, label }: { href: string; label: string }) => (
  <Link
    href={href}
    isExternal
    display="inline-flex"
    alignItems="center"
    gap={1.5}
    fontSize="sm"
    fontWeight="medium"
    color="blue.500"
    _hover={{ color: "blue.600", textDecoration: "underline" }}
  >
    <Icon as={Download} boxSize={3.5} flexShrink={0} />
    {label}
  </Link>
);

// ─── FileListLinks ─────────────────────────────────────────────────────────────
// Renderiza os múltiplos arquivos de um campo file_list (value = JSON array de
// URLs). Cada item vira um link de download numerado.

const FileListLinks = ({ value, label }: { value: string | null | undefined; label: string }) => {
  const urls = parseFileListValue(value);
  if (urls.length === 0) {
    return <Text fontSize="sm" color="gray.400">NÃO INFORMADO</Text>;
  }
  return (
    <VStack align="stretch" spacing={1.5}>
      {urls.map((url, idx) => (
        <FileLink key={url + idx} href={url} label={`${label?.trim() || "Arquivo"} ${idx + 1}`} />
      ))}
    </VStack>
  );
};

// ─── FormSubmission ────────────────────────────────────────────────────────────

const FormSubmission = ({ fields }: { fields: FormField[] }) => {
  const valueColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.400", "whiteAlpha.500");
  return (
    <VStack align="stretch" spacing={3}>
      {fields.map((f, i) => {
        const ft = (f.field_type ?? "").toUpperCase();
        const isFileList = ft === "FILE_LIST";
        return (
          <Box key={f.key ?? i}>
            <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wider" mb={0.5}>
              {f.label?.trim() || "Campo"}
            </Text>
            {isFileList ? (
              <FileListLinks value={f.value} label={f.label} />
            ) : isFileLink(f.value) ? (
              <FileLink href={f.value!} label={f.label?.trim() || "Baixar Arquivo"} />
            ) : (
              <Text fontSize="sm" color={valueColor} fontWeight="medium">{formatValue(f.value)}</Text>
            )}
            {i < fields.length - 1 && <Divider mt={3} />}
          </Box>
        );
      })}
    </VStack>
  );
};

// ─── FilesSubmission ───────────────────────────────────────────────────────────

const FilesSubmission = ({ files }: { files: FileItem[] }) => {
  const valueColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.400", "whiteAlpha.500");
  return (
    <VStack align="stretch" spacing={3}>
      {files.map((file, i) => {
        const isFileList = (file.field_type ?? "").toUpperCase() === "FILE_LIST";
        return (
          <Box key={file.key ?? i}>
            <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wider" mb={0.5}>
              {file.label?.trim() || "Arquivo"}
            </Text>
            {isFileList ? (
              <FileListLinks value={file.value} label={file.label} />
            ) : isFileLink(file.value) ? (
              <FileLink href={file.value!} label={file.label?.trim() || "Baixar Arquivo"} />
            ) : (
              <Text fontSize="sm" color={valueColor} fontWeight="medium">{formatValue(file.value)}</Text>
            )}
            {i < files.length - 1 && <Divider mt={3} />}
          </Box>
        );
      })}
    </VStack>
  );
};

// ─── ExternalSubmission ────────────────────────────────────────────────────────

const ExternalSubmission = ({ note }: { note: string }) => {
  const bg          = useColorModeValue("green.50", "rgba(72,187,120,0.1)");
  const borderColor = useColorModeValue("green.200", "green.700");
  const textColor   = useColorModeValue("green.700", "green.300");
  return (
    <Box p={4} bg={bg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
      <Text fontSize="sm" color={textColor}>{note}</Text>
    </Box>
  );
};

// ─── EditFieldInput ────────────────────────────────────────────────────────────

export interface EditFieldInputProps {
  field: FormField;
  value: string;
  onChange: (v: string) => void;
}

export const EditFieldInput = ({ field, value, onChange }: EditFieldInputProps) => {
  const inputBg = useColorModeValue("white", "gray.700");
  const ft = (field.field_type ?? "").toUpperCase();

  if (ft === "BOOLEAN") {
    return (
      <ChakraSelect value={value} onChange={(e) => onChange(e.target.value)} bg={inputBg} size="sm">
        <option value="">Selecione...</option>
        <option value="true">Sim</option>
        <option value="false">Não</option>
      </ChakraSelect>
    );
  }

  if (ft === "SELECT" || ft === "MULTI_SELECT") {
    const opts: any[] = field.options ?? [];
    return (
      <ChakraSelect value={value} onChange={(e) => onChange(e.target.value)} bg={inputBg} size="sm">
        <option value="">Selecione...</option>
        {opts.map((o, i) => {
          const optVal   = typeof o === "object" ? (o.value ?? o.label ?? String(o)) : String(o);
          const optLabel = typeof o === "object" ? (o.label ?? o.value ?? String(o)) : String(o);
          return <option key={i} value={optVal}>{optLabel}</option>;
        })}
      </ChakraSelect>
    );
  }

  if (ft === "DATE") {
    const dateVal = value && ISO_DATE_RE.test(value.trim()) ? value.trim().split("T")[0] : value;
    return (
      <Input
        type="date"
        value={dateVal}
        onChange={(e) => onChange(e.target.value)}
        bg={inputBg}
        size="sm"
      />
    );
  }

  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      bg={inputBg}
      size="sm"
      placeholder="Digite o valor..."
    />
  );
};

// ─── EditFormSubmission ────────────────────────────────────────────────────────

export interface EditFormSubmissionProps {
  fields: FormField[];
  editValues: Record<string, string>;
  onChangeField: (key: string, value: string) => void;
}

export const EditFormSubmission = ({ fields, editValues, onChangeField }: EditFormSubmissionProps) => {
  const labelColor = useColorModeValue("gray.400", "whiteAlpha.500");
  const valueColor = useColorModeValue("gray.800", "white");

  return (
    <VStack align="stretch" spacing={4}>
      {fields.map((f, i) => {
        const ft = (f.field_type ?? "").toUpperCase();
        const isFile = ft === "FILE";
        const isFileList = ft === "FILE_LIST";
        // Arquivos (file e file_list) não são editáveis como texto — só download.
        const isReadOnlyFile = isFile || isFileList;
        return (
          <Box key={f.key ?? i}>
            <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wider" mb={1}>
              {f.label?.trim() || "Campo"}
            </Text>
            {isReadOnlyFile ? (
              isFileList ? (
                <FileListLinks value={f.value} label={f.label} />
              ) : isFileLink(f.value) ? (
                <FileLink href={f.value!} label={f.label?.trim() || "Baixar Arquivo"} />
              ) : (
                <Text fontSize="sm" color={valueColor} fontWeight="medium">{formatValue(f.value)}</Text>
              )
            ) : (
              <EditFieldInput
                field={f}
                value={editValues[f.key] ?? ""}
                onChange={(v) => onChangeField(f.key, v)}
              />
            )}
            {i < fields.length - 1 && <Divider mt={4} />}
          </Box>
        );
      })}
    </VStack>
  );
};

// ─── MissionSubmission (componente raiz / export padrão) ──────────────────────

export interface MissionSubmissionProps {
  submission: Submission | null;
}

const MissionSubmission = ({ submission }: MissionSubmissionProps) => {
  const emptyBg        = useColorModeValue("gray.50", "whiteAlpha.100");
  const emptyTextColor = useColorModeValue("gray.500", "whiteAlpha.600");

  if (!submission) return (
    <Box p={4} bg={emptyBg} borderRadius="md">
      <Text fontSize="sm" color={emptyTextColor}>Missão ainda não preenchida pelo candidato.</Text>
    </Box>
  );
  if (submission.type === "form")     return <FormSubmission fields={submission.fields} />;
  if (submission.type === "files")    return <FilesSubmission files={submission.files} />;
  if (submission.type === "external") return <ExternalSubmission note={submission.note} />;
  if (submission.type === "mixed")    return (
    <VStack align="stretch" spacing={4}>
      <FormSubmission fields={submission.fields} />
      <FilesSubmission files={submission.files} />
    </VStack>
  );
  return null;
};

export default MissionSubmission;
